import PropTypes from 'prop-types';
import { memo, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// material
import { alpha, styled, useTheme } from '@material-ui/core/styles';
import { Box, Link, Stack, Button, Drawer, Tooltip, Typography, CardActionArea, Divider } from '@material-ui/core';
// hooks
import useAuth from '../../hooks/useAuth';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
// routes
import { PATH_DASHBOARD, PATH_DOCS, PATH_PAGE } from '../../routes/paths';
// components
import Logo from '../../components/Logo';
import MyAvatar from '../../components/MyAvatar';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import { MHidden } from '../../components/@material-extend';
//

import sidebarConfig from './SidebarConfig';
import { DocIllustration } from '../../assets';
//import LogoCustom from '../../components/LogoCustom';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.complex
    })
  }
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.shape.borderRadiusSm,
  backgroundColor: theme.palette.grey[500_12]
}));

// ----------------------------------------------------------------------

IconCollapse.propTypes = {
  onToggleCollapse: PropTypes.func,
  collapseClick: PropTypes.bool
};

function IconCollapse({ onToggleCollapse, collapseClick }) {

  return (
    <Tooltip title="Mini Menu">
      <CardActionArea
        onClick={onToggleCollapse}
        sx={{
          width: 18,
          height: 18,
          display: 'flex',
          cursor: 'pointer',
          borderRadius: '50%',
          alignItems: 'center',
          color: 'text.primary',
          justifyContent: 'center',
          border: 'solid 1px currentColor',
          ...(collapseClick && {
            borderWidth: 2
          })
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'currentColor',
            transition: (theme) => theme.transitions.create('all'),
            ...(collapseClick && {
              width: 0,
              height: 0
            })
          }}
        />
      </CardActionArea>
    </Tooltip>
  );
}



export default memo(function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const theme = useTheme();
  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
     
      <Stack
        spacing={3}
        style={{
          borderRight: 'solid 2px',
          '&:before': {
            borderRight: 'solid 2px red'
          }

        }}
        sx={{
          px: 2.5,
          pt: 3,
          pb: 2,
          ...(isCollapse && {
            alignItems: 'center'
          })
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent={isCollapse ? "space-between" : "space-between"}>
          <Box component={RouterLink} to="/" sx={{ display: 'inline-flex' }}>
            <Logo width={isCollapse ? 80 : 160} height={isCollapse ? 'auto' : 'auto'} />
          </Box>

          <MHidden width="lgDown">
            {!isCollapse && <IconCollapse onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />}
          </MHidden>
        </Stack>

        {isCollapse ? (
          <MyAvatar sx={{ mx: 'auto', mb: 2 }} />
        ) : (
          <Link underline="none" component={RouterLink} to="/">
            <AccountStyle>
              {/* <MyAvatar /> */}

              <Box
                component="img"
                src="/static/icons/navbar/icon-dashboard.svg"
                sx={{
                  zIndex: 9,
                  width: 24,
                  height: 24,
                  filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.24))'
                }}
              />
              <Box sx={{ ml: 2 }}>
                <Typography variant="subtitle2" sx={{ color: theme.palette.text.sidebar }}>
                  DASHBOARD
                </Typography>
              </Box>

            </AccountStyle>

          </Link>
        )}
      </Stack>

      <NavSection navConfig={sidebarConfig} isShow={!isCollapse} />

      <Box sx={{ flexGrow: 1 }} />

      {/* {!isCollapse && (
        <Stack spacing={3} alignItems="center" sx={{ px: 5, pb: 5, mt: 10, width: 1, textAlign: 'center' }}>
          <DocIllustration sx={{ width: 1 }} />

          <div>
            <Typography gutterBottom variant="subtitle1">
              Hi, {user?.displayName}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Need help?
              <br /> Please check our docs
            </Typography>
          </div>
          <Button href={PATH_DOCS} target="_blank" variant="contained">
            Documentation
          </Button>
        </Stack>
      )} */}
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH
        },
        ...(collapseClick && {
          position: 'absolute'
        })
      }}
    >
      
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: (theme) => alpha(theme.palette.background.sidebar, 0.88)
            }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.sidebar',
              ...(isCollapse && {
                width: COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                borderRight: 0,
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
                boxShadow: (theme) => theme.customShadows.z20,
                bgcolor: (theme) => alpha(theme.palette.background.sidebar, 0.88)
              })
            }
          }}
        >
          {renderContent}
          {!isCollapse && (
            <Stack spacing={1}
              alignItems="center"
              sx={{ px: 2, pb: 2, mt: 6, width: 1, }}>
              {/* <DocIllustration sx={{ width: 1 }} /> */}

              <div>
                {/* <Typography gutterBottom variant="subtitle1">
                  Hi, {user?.displayName}
                </Typography> */}

                <Box
                  component="img"
                  src="/static/qr_oa.jpg"
                  sx={{
                    zIndex: 9,
                    position: 'relative',
                    left: 70,
                    width: 60,
                    filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.24))'
                  }}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: '100', color: '#1ccaff' }}>
                  {/* Power by  <b>VOIP24H</b> */}
                  <br /> Liên hệ hỗ trợ Kĩ thuật : 1900.2000
                  {/* <br /> Vietnam Voip Solution - V.1.0.2023 */}
                </Typography>
              </div>

            </Stack>
          )}
          {!isCollapse &&
            <Box sx={{ width: "100%" }}>
              <Box sx={{ height: "15px", width: '75%', float: "right", background: "#41c7f5", clipPath: 'polygon(0 0, 100% 0, 100% 100%, 5% 100%)' }}> </Box>
              <Box sx={{ height: "15px", width: '40%', background: "red", clipPath: 'polygon(0 0, 83% 0, 100% 100%, 0 100%)' }}> </Box>
            </Box>
          }
        </Drawer>
      </MHidden>
    </RootStyle>
  );
})